import api, { setOnNewTokensCallback } from '@/api/api';
import { AuthTokens, useLogoutMutation } from '@/api/mutations/auth';
import { authChannel } from '@/lib/broadcastChannels';
import { setAccessToken, setIdToken } from '@/lib/tokenManager';
import { queryClient } from '@/providers/QueryProvider';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export interface TAuthContext {
  loginSession: string;
  setLoginSession: (session: string) => void;
  username: string;
  setUsername: (username: string) => void;
  tokens?: AuthTokens | null;
  setAuthTokens: (tokens: AuthTokens | null) => void;
  logout: () => void;
  isLoading?: boolean;
  loginSessionExpired?: boolean;
  setLoginSessionExpired: (expired: boolean) => void;
  authSessionExpired: () => void;
}

export const AuthContext = createContext<TAuthContext>({} as TAuthContext);
export type TAuthInitialData = Omit<
  TAuthContext,
  'setLoginSession' | 'setUsername' | 'setAuthTokens' | 'logout' | 'setLoginSessionExpired' | 'authSessionExpired'
>;

export function AuthProvider({
  children,
  initialData,
}: {
  children: React.ReactNode;
  initialData?: TAuthInitialData;
}) {
  const [ loginSession, setLoginSession ] = useState(
    initialData?.loginSession ?? '',
  );
  const [ username, setUsername ] = useState(initialData?.username ?? '');
  const [ tokens, setTokens ] = useState<AuthTokens | null>(
    initialData?.tokens ?? null,
  );
  const [ isLoading, setIsLoading ] = useState(true);
  const [ loginSessionExpired, setLoginSessionExpired ] = useState(false);
  const logoutMutation = useLogoutMutation();

  const setAuthTokens = (tokens: AuthTokens | null) => {
    setAccessToken(tokens?.accessToken ?? null);
    setIdToken(tokens?.idToken ?? null);
    setTokens(tokens ?? null);
    authChannel.postMessage(tokens);
    void queryClient.invalidateQueries({ queryKey: [ 'currentUser' ] });
  };

  const clearLoginState = () => {
    setAuthTokens(null);
    setAccessToken(null);
    setIdToken(null);
    setLoginSession('');
    queryClient.clear();
  };

  const logout = () => {
    setIsLoading(true);
    logoutMutation.mutate(undefined, {
      onSettled: () => {
        clearLoginState();
        setIsLoading(false);
      },
    });
  };

  const authSessionExpired = () => {
    setLoginSessionExpired(true);
    setLoginSession('');
    setUsername('');
  };

  // Register the callback to update access token in memory
  useEffect(() => {
    setOnNewTokensCallback(setAuthTokens);
  }, []);

  // This useEffect runs once on app startup to check whether user is authenticated
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (tokens) return;

      try {
        setIsLoading(true);

        const response = await api
          .post('auth/refresh-token', { credentials: 'include' })
          .json<{ tokens: AuthTokens }>();
        setAuthTokens(response.tokens);
      }
      catch {
        setAuthTokens(null);
      }
      finally {
        setIsLoading(false);
      }
    };

    void checkAuthStatus();
  }, []);

  // Listen for auth messages from other tabs
  useEffect(() => {
    authChannel.onmessage = (event: MessageEvent<AuthTokens | null>) => {
      setTokens(event.data);
      setAccessToken(event.data?.accessToken ?? null);
      setIdToken(event.data?.idToken ?? null);
      void queryClient.invalidateQueries({ queryKey: [ 'currentUser' ] });
    };

    return () => {
      authChannel.onmessage = null;
      authChannel.close();
    };
  }, []);

  const value = useMemo(() => {
    return {
      loginSession,
      setLoginSession,
      username,
      setUsername,
      tokens,
      setAuthTokens,
      logout,
      isLoading,
      loginSessionExpired,
      setLoginSessionExpired,
      authSessionExpired,
    };
  }, [
    loginSession,
    setLoginSession,
    username,
    setUsername,
    tokens,
    setAuthTokens,
    logout,
    isLoading,
    loginSessionExpired,
    setLoginSessionExpired,
    authSessionExpired,
  ]);

  return <AuthContext.Provider value={ value }>{ children }</AuthContext.Provider>;
}

export const useAuth = () => {
  const { tokens, setAuthTokens, logout, isLoading } = useContext(AuthContext);
  return { tokens, setAuthTokens, logout, isLoading };
};
